<template>
    <div class="wa-business-daily-usage">
        <md-progress-bar md-mode="indeterminate" v-if="loading" />
        <div class="header">
            <div class="title">{{$t("whatsapp_business_api.dailyUsageReport")}}</div>
        </div>
        <div class="title">{{$t("whatsapp_business_api.dailyUsageReport")}}.</div>
        <div class="table-toolbar">
            <div class="date-filter d-flex gap-5">
                <i class="material-icons">date_range</i>
                <select id="time-select" v-model="timeSelect" @change="setCustomDateFilter(timeSelect)">
                    <option value="today">{{ $t("time.today") }}</option>
                    <option value="yesterday">{{ $t("time.yesterday") }}</option>
                    <option value="current-week">{{ $t("time.currentWeek") }}</option>
                    <option value="last-week">{{ $t("time.lastWeek") }}</option>
                    <option value="current-month">{{ $t("time.currentMonth") }}</option>
                    <option value="last-month">{{ $t("time.lastMonth") }}</option>
                    <option value="last-30">{{ $t("time.last30Day") }}</option>
                    <option v-for="(m, i) in getLast11Months()" :key="i" :value="m.date">
                        {{ m.name }}
                    </option>
                </select>
            </div>

            <div class="d-flex justify-content-end w-100">
                <a-pagination
                    :key="Math.random()"
                    size="small"
                    :total="total"
                    :show-total="(total) => `Total ${total} record`"
                    v-model="currentPage"
                    show-size-changer
                    show-quick-jumper
                    :pageSizeOptions="['10', '20', '50', '100', '300', '500', '1000']"
                    :defaultPageSize="maxResult"
                    @showSizeChange="(current, size) => (maxResult = size)"
                    :disabled="loading"
                />
            </div>
        </div>

        <a-table
            :columns="columns"
            :data-source="getRows()"
            :pagination="false"
            row-key="date"
            size="small"
            :rowClassName="getRowClassName"
        >
            <span slot="date" slot-scope="text, record">
                {{ $moment(record.date).format("YYYY-MM-DD") }}
            </span>
            <span slot="price" slot-scope="text, record">
                {{ getPrice(record) }}
            </span>
        </a-table>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import _ from "lodash";

import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import log2 from "../../../../utils/log2";
import IconButton from "../../Settings/components/IconButton.vue";
import SYSTEM_PUBSUB from "../../../../utils/systemPubSub";
import { filter, first, timeout } from "rxjs/operators";
import moment from "moment";

const log = log2("DailyUsages.vue");
export default Vue.extend({
    name: "DailyUsages",
    components: { IconButton },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            total: 0,
            currentPage: 1,
            maxResult: 100,
            usages: [],
            from: moment().startOf("month").format("YYYY-MM-DD"),
            to: moment().endOf("month").format("YYYY-MM-DD"),
            timeSelect: "current-month",
            columns: [
                {
                    title: this.$t("message.date"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: this.$t("whatsapp_business_api.freeChat"),
                    dataIndex: "freeConversationCount",
                    key: "freeConversationCount",
                },
                {
                    title: this.$t("whatsapp_business_api.paidChat"),
                    dataIndex: "paidConversationCount",
                    key: "paidConversationCount",
                },
                {
                    title: this.$t("whatsapp_business_api.templateMessage"),
                    dataIndex: "sentTemplateMessageCount",
                    key: "sentTemplateMessageCount",
                },
                {
                    title: this.$t("whatsapp_business_api.outgoingSession"),
                    dataIndex: "outgoingSessionMessageCount",
                    key: "outgoingSessionMessageCount",
                },
                {
                    title: this.$t("whatsapp_business_api.incomingSession"),
                    dataIndex: "incomingSessionMessageCount",
                    key: "incomingSessionMessageCount",
                },
                {
                    title:  this.$t("whatsapp_business_api.totalCost"),
                    dataIndex: "totalPricing",
                    key: "totalPricing",
                    scopedSlots: { customRender: "price" },
                },
            ],
        };
    },
    computed: {
        ...mapState({
            socketConnected: (state) => state.socketStatus.socket_connected,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        getRowClassName(row, index) {
            log("getRowClassName", row, index);
            return row.date === "Total" ? "total" : "";
        },
        getRows() {
            const usages = this.usages;
            const total = this.calculateTotalRow();
            return [...usages, ...total];
        },
        calculateTotalRow() {
            let total = 0;
            const result = this.usages.reduce(
                (acc, cur) => {
                    acc.freeConversationCount += cur.freeConversationCount;
                    acc.paidConversationCount += cur.paidConversationCount;
                    acc.sentTemplateMessageCount += cur.sentTemplateMessageCount;
                    acc.outgoingSessionMessageCount += cur.outgoingSessionMessageCount;
                    acc.incomingSessionMessageCount += cur.incomingSessionMessageCount;
                    acc.totalPricing += cur.totalPricing;
                    return acc;
                },
                {
                    date: "Total",
                    freeConversationCount: 0,
                    paidConversationCount: 0,
                    sentTemplateMessageCount: 0,
                    outgoingSessionMessageCount: 0,
                    incomingSessionMessageCount: 0,
                    totalPricing: 0,
                }
            );
            return [result];
        },
        setCustomDateFilter(filter) {
            // log("setCustomDateFilter ", filter);
            if (filter === "today") {
                this.from = moment().startOf("day").format("YYYY-MM-DD");
                this.to = moment().endOf("day").format("YYYY-MM-DD");
            } else if (filter === "yesterday") {
                this.from = moment().subtract(1, "days").startOf("day").format("YYYY-MM-DD");
                this.to = moment().subtract(1, "days").endOf("day").format("YYYY-MM-DD");
            } else if (filter === "current-week") {
                this.from = moment().startOf("week").startOf("day").format("YYYY-MM-DD");
                this.to = moment().endOf("week").endOf("day").format("YYYY-MM-DD");
            } else if (filter === "last-week") {
                this.from = moment().startOf("isoWeeks").subtract(1, "days").startOf("isoWeeks").format("YYYY-MM-DD");
                this.to = moment().startOf("isoWeeks").subtract(1, "days").endOf("isoWeeks").format("YYYY-MM-DD");
            } else if (filter === "current-month") {
                this.from = moment().startOf("month").startOf("day").format("YYYY-MM-DD");
                this.to = moment().endOf("month").endOf("day").format("YYYY-MM-DD");
            } else if (filter === "last-month") {
                this.from = moment().startOf("month").subtract(1, "days").startOf("month").format("YYYY-MM-DD");
                this.to = moment().startOf("month").subtract(1, "days").endOf("month").format("YYYY-MM-DD");
            } else if (filter === "last-30") {
                this.from = moment().subtract(30, "days").startOf("day").format("YYYY-MM-DD");
                this.to = moment().endOf("day").format("YYYY-MM-DD");
            } else if (typeof filter === "object") {
                this.from = filter.start;
                this.to = filter.end;
            }
            this.getDailyUsages();
        },
        getLast11Months() {
            let months = [];
            for (let i = 1; i < 12; i++) {
                let date = moment().subtract(i, "months");
                months.push({
                    name: date.format("MMMM YYYY"),
                    date: {
                        start: date.startOf("month").utc().format("YYYY-MM-DD"),
                        end: date.endOf("month").utc().format("YYYY-MM-DD"),
                    },
                });
            }

            return months;
        },
        listenSocketMessages() {
            this.subscriber = socketMessageStream.subscribe((message) => {
                if (message.cmd === "whatsapp_daily_usage_response") {
                    log("whatsapp_daily_usage_response=>", message);
                    this.usages = _.orderBy(message.data.usages, (q) => q.date, "desc");

                    this.total = message.data.usages.length;
                    this.loading = false;
                }
            });
        },
        getPrice(row) {
            return `$ ${row.totalPricing.toFixed(2)}`;
        },
        getDailyUsages() {
            this.loading = true;
            sendSocketMessage("whatsapp get-daily-usage", {
                channelId: this.value._id,
                siteId: this.site._id,
                from: this.from,
                to: this.to,
            });
        },
        nextPage() {
            if (this.currentPage * this.maxResult < this.count) {
                this.currentPage++;
                // this.getDailyUsages();
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                // this.getDailyUsages();
            }
        },
    },
    mounted() {
        this.listenSocketMessages();
        this.getDailyUsages();
    },
    watch: {
        from() {
            // this.getDailyUsages();
        },
        to() {
            // this.getDailyUsages();
        },
        socketConnected(isConnected) {
            if (isConnected) {
                this.getDailyUsages();
            }
        },
    },
});
</script>

<style lang="scss">
.wa-business-daily-usage {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    position: relative;

    .md-progress-bar {
        position: absolute;
        top: 0;
        width: 100%;
        margin-left: -20px;
    }

    .table-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ant-table-wrapper {
        .ant-table-row {
            &.total {
                background: #f6c26e;

                td {
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                }
            }
        }
    }

    .table-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 20px;
        border-top: 1px solid var(--color-border-default);
        font-weight: 700;
        font-size: 14px;
        color: var(--color-text-strong-default);

        strong {
            width: 100%;
            text-align: left;
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 10px;

        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            color: var(--color-text-strong-default);
        }
    }
}
</style>
